import { useEffect } from "react";

// Inject an FAQPage JSON-LD block, canonical link, and OpenGraph meta
// tags into document.head. Cleans up on unmount so navigating between
// resource pages never leaks stale schema.

function upsertMeta(nameOrProperty, content, attrKey = "name", id) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("meta");
    el.id = id;
    el.setAttribute(attrKey, nameOrProperty);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink(rel, href, id) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("link");
    el.id = id;
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export default function FaqSchema({ id, faqs, title, description, canonicalUrl }) {
  useEffect(() => {
    const idPrefix = `resource-${id}`;
    const scriptId = `${idPrefix}-jsonld`;
    document.getElementById(scriptId)?.remove();

    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (faqs || []).slice(0, 3).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.a,
        },
      })),
    };
    const s = document.createElement("script");
    s.id = scriptId;
    s.type = "application/ld+json";
    s.text = JSON.stringify(data);
    document.head.appendChild(s);

    // Title + meta
    const prevTitle = document.title;
    if (title) document.title = title;
    if (description) {
      upsertMeta("description", description, "name", `${idPrefix}-desc`);
      upsertMeta("og:description", description, "property", `${idPrefix}-ogdesc`);
      upsertMeta("twitter:description", description, "name", `${idPrefix}-twdesc`);
    }
    if (title) {
      upsertMeta("og:title", title, "property", `${idPrefix}-ogtitle`);
      upsertMeta("twitter:title", title, "name", `${idPrefix}-twtitle`);
    }
    upsertMeta("og:type", "article", "property", `${idPrefix}-ogtype`);
    upsertMeta("twitter:card", "summary_large_image", "name", `${idPrefix}-twcard`);
    if (canonicalUrl) {
      upsertLink("canonical", canonicalUrl, `${idPrefix}-canonical`);
      upsertMeta("og:url", canonicalUrl, "property", `${idPrefix}-ogurl`);
    }

    return () => {
      document.title = prevTitle;
      document.getElementById(scriptId)?.remove();
      [
        `${idPrefix}-desc`, `${idPrefix}-ogdesc`, `${idPrefix}-twdesc`,
        `${idPrefix}-ogtitle`, `${idPrefix}-twtitle`, `${idPrefix}-ogtype`,
        `${idPrefix}-twcard`, `${idPrefix}-canonical`, `${idPrefix}-ogurl`,
      ].forEach((k) => document.getElementById(k)?.remove());
    };
  }, [id, faqs, title, description, canonicalUrl]);

  return null;
}
